<template>
  <div class="language-switcher relative">
    <!-- Language Switcher Button -->
    <button
      @click="toggleDropdown"
      @touchend.prevent="handleMobileTouch"
      class="language-button"
      :disabled="isLoading"
      aria-label="Switch Language"
    >
      <div class="flex items-center space-x-2">
        <span class="language-flag" :title="getCurrentLocaleInfo.native">
          <svg v-if="getCurrentLocaleInfo.code === 'en'" class="w-5 h-5 rounded-sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30">
            <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
            <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
            <g clip-path="url(#s)">
              <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
              <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/>
              <path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#t)" stroke="#C8102E" stroke-width="4"/>
              <path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/>
              <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/>
            </g>
          </svg>
          <svg v-else-if="getCurrentLocaleInfo.code === 'it'" class="w-5 h-5 rounded-sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2">
            <rect width="1" height="2" fill="#009246"/>
            <rect width="1" height="2" x="1" fill="#fff"/>
            <rect width="1" height="2" x="2" fill="#ce2b37"/>
          </svg>
          <svg v-else-if="getCurrentLocaleInfo.code === 'fr'" class="w-5 h-5 rounded-sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2">
            <rect width="1" height="2" fill="#002395"/>
            <rect width="1" height="2" x="1" fill="#fff"/>
            <rect width="1" height="2" x="2" fill="#ED2939"/>
          </svg>
        </span>
        <svg
          class="dropdown-arrow transition-transform duration-200"
          :class="{ 'rotate-180': isDropdownOpen }"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="m6 9 6 6 6-6"></path>
        </svg>
      </div>
      <div v-if="isLoading" class="loading-spinner"></div>
    </button>

    <!-- Dropdown Menu -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isDropdownOpen"
        class="language-dropdown"
      >
        <div class="dropdown-content">
          <button
            v-for="langInfo in getAvailableLocalesInfo"
            :key="langInfo.code"
            @click="switchLanguage(langInfo.code)"
            class="language-option"
            :class="{ 'active': langInfo.isCurrent }"
            :disabled="langInfo.isCurrent || isLoading"
          >
            <div class="flex items-center space-x-2">
              <span class="language-flag text-base">
                <svg v-if="langInfo.code === 'en'" class="w-5 h-5 rounded-sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30">
                  <clipPath id="s2"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
                  <clipPath id="t2"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
                  <g clip-path="url(#s2)">
                    <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
                    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/>
                    <path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#t2)" stroke="#C8102E" stroke-width="4"/>
                    <path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/>
                    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/>
                  </g>
                </svg>
                <svg v-else-if="langInfo.code === 'it'" class="w-5 h-5 rounded-sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2">
                  <rect width="1" height="2" fill="#009246"/>
                  <rect width="1" height="2" x="1" fill="#fff"/>
                  <rect width="1" height="2" x="2" fill="#ce2b37"/>
                </svg>
                <svg v-else-if="langInfo.code === 'fr'" class="w-5 h-5 rounded-sm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2">
                  <rect width="1" height="2" fill="#002395"/>
                  <rect width="1" height="2" x="1" fill="#fff"/>
                  <rect width="1" height="2" x="2" fill="#ED2939"/>
                </svg>
              </span>
              <span class="language-code">{{ langInfo.code.toUpperCase() }}</span>
            </div>
            <svg
              v-if="langInfo.isCurrent"
              class="check-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 12 2 2 4-4"></path>
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  // Filter which locales to show (e.g., ['en', 'fr'] for admin)
  allowedLocales: {
    type: Array,
    default: () => ['en', 'it', 'fr']
  }
})

const isDropdownOpen = ref(false)
const isLoading = ref(false)
let isMobileDevice = false
let isToggling = false

const { locale, setLocale } = useI18n()

const languageInfo = {
  en: {
    code: 'en',
    name: 'English',
    native: 'English',
    flag: '🇬🇧',
    flagIcon: 'gb',
    flagText: 'GB'
  },
  it: {
    code: 'it',
    name: 'Italian',
    native: 'Italiano',
    flag: '🇮🇹',
    flagIcon: 'it',
    flagText: 'IT'
  },
  fr: {
    code: 'fr',
    name: 'French',
    native: 'Français',
    flag: '🇫🇷',
    flagIcon: 'fr',
    flagText: 'FR'
  }
}

const getCurrentLocaleInfo = computed(() => {
  return languageInfo[locale.value] || languageInfo.en
})

const getAvailableLocalesInfo = computed(() => {
  return Object.values(languageInfo)
    .filter(lang => props.allowedLocales.includes(lang.code))
    .map(lang => ({
      ...lang,
      isCurrent: lang.code === locale.value
    }))
})

const supportsEmojiFlags = ref(true)

onMounted(() => {
  const testCanvas = document.createElement('canvas')
  const testContext = testCanvas.getContext('2d')
  if (testContext) {
    testContext.font = '16px Arial'
    const metrics = testContext.measureText('🇬🇧')
    supportsEmojiFlags.value = metrics.width > 10
  }
})

const handleMobileTouch = (event) => {
  if (isMobileDevice) {
    event.preventDefault()
    toggleDropdown(event)
  }
}

const toggleDropdown = async (event) => {
  if (isToggling) return
  isToggling = true

  event?.stopPropagation()
  if (!isMobileDevice) {
    event?.preventDefault()
  }

  isDropdownOpen.value = !isDropdownOpen.value

  setTimeout(() => {
    isToggling = false
  }, 200)
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

const switchLanguage = async (localeCode) => {
  if (localeCode === locale.value || isLoading.value) return

  try {
    isLoading.value = true

    await setLocale(localeCode)

    if (typeof window !== 'undefined') {
      localStorage.setItem('locale', localeCode)

      document.cookie = `locale=${localeCode}; path=/; max-age=31536000; SameSite=Lax`
    }


    closeDropdown()
  } catch (error) {
  } finally {
    isLoading.value = false
  }
}

let clickOutsideHandler = null

onMounted(() => {
  isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  clickOutsideHandler = (event) => {
    if (isToggling) return

    const switcher = document.querySelector('.language-switcher')

    if (switcher && !switcher.contains(event.target)) {
      const delay = isMobileDevice ? 150 : 50

      setTimeout(() => {
        if (isDropdownOpen.value && !isToggling) {
          closeDropdown()
        }
      }, delay)
    }
  }

  const eventType = isMobileDevice ? 'touchstart' : 'mousedown'
  document.addEventListener(eventType, clickOutsideHandler, { passive: true })
})

onUnmounted(() => {
  if (clickOutsideHandler) {
    const eventType = isMobileDevice ? 'touchstart' : 'mousedown'
    document.removeEventListener(eventType, clickOutsideHandler)
  }
})
</script>

<style scoped>
.language-switcher {
  position: relative;
  display: inline-block;
}

.language-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  transition: all 0.2s;
}

.language-button:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  border-color: #d1d5db;
}

.language-button:focus {
  outline: none;
  ring: 2px;
  ring-color: #3b82f6;
  border-color: transparent;
}

.language-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.language-flag {
  font-size: 1.125rem;
  line-height: 1;
  display: inline-block;
  min-width: 1.5rem;
  text-align: center;
}

.flag-emoji {
  font-size: 1.25rem;
  line-height: 1;
  display: inline-block;
}

.flag-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #d1d5db;
  display: inline-block;
  min-width: 1.5rem;
  text-align: center;
}

.language-code {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.language-option .language-code {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
}

.language-option.active .language-code {
  color: #2563eb;
}

.dropdown-arrow {
  width: 1rem;
  height: 1rem;
  color: #6b7280;
}

.loading-spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.25rem;
}

.loading-spinner::after {
  content: '';
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid #3b82f6;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.language-dropdown {
  position: absolute;
  right: 0;
  margin-top: 0.5rem;
  width: 7rem;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px -3px rgb(0 0 0 / 0.15), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  z-index: 50;
}

.dropdown-content {
  padding: 0.25rem 0;
}

.language-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  text-align: left;
  transition: all 0.15s;
}

.language-option:hover {
  background-color: #eff6ff;
}

.language-option:focus {
  outline: none;
  background-color: #eff6ff;
}

.language-option.active {
  background-color: #dbeafe;
  color: #2563eb;
}

.language-option:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.language-info {
  display: flex;
  flex-direction: column;
}

.language-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
}

.language-native {
  font-size: 0.75rem;
  color: #6b7280;
}

.language-option.active .language-name {
  color: #2563eb;
}

.language-option.active .language-native {
  color: #3b82f6;
}

.check-icon {
  color: #2563eb;
}

/* Mobile styles */
@media (max-width: 640px) {
  .language-dropdown {
    width: 6rem;
    left: auto;
    right: 0;
    z-index: 100;
  }

  .language-button {
    min-height: 44px;
    min-width: 44px;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .language-option {
    padding: 0.75rem 0.5rem;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
}
</style>
